import dynamic from 'next/dynamic'
import { rem } from 'polished'
import React from 'react'
import { formatPrice } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled, { css } from 'styled-components'
import { ProductImage } from '@public/components/ProductImage'
import { Button, BUTTON_VARIANT, BUTTON_ICON_POSITION } from 'components/Button'
import { Card, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon'
import { Box, Flex } from 'components/Layout'
import { type ModalProps } from 'components/Modal'
import { ProductOptionRow } from 'components/ProductOptionRow'
import { Text, H6, H5 } from 'components/Typography'
import {
  FONT_SIZE,
  SPACE,
  FONT_WEIGHT,
  IMAGE_BASE_WIDTH,
  COLOR_INTENT,
} from 'Theme'
import { type Price, type PublicProductQuery } from 'types/graphql-generated'
import { formatLeadTimeString, formatQuantityWithUnit } from 'utils/formatters'

const SlideOutModal = dynamic(() => import('components/Modal/SlideOutModal'), {
  ssr: false,
})

const MODAL_TITLE = 'Added to cart'

const StyledSlideOutModal = styled(SlideOutModal)`
  & > div:nth-of-type(2) {
    padding-top: 0;
    padding-bottom: 0;
  }
`

const ProductDescription = styled(Text)`
  font-size: ${FONT_SIZE.PX_12};
  color: ${COLOR_INTENT.GRAY_70};
`
interface CartModalProps extends Omit<ModalProps, 'children'> {
  title: string
  price?: Pick<Price, 'total' | 'currencyIsoCode'>
  tradePrice?: Pick<Price, 'total' | 'currencyIsoCode'>
  optionValues: PublicProductQuery['product']['variants'][0]['optionValues']
  cartQuantity: number
  quantityUnit?: {
    singular: string
    plural: string
  }
  cartTotalPrice?: Pick<Price, 'total' | 'currencyIsoCode'>
  cartItemsCount: number
  imageSrc: string
  imageAlt: string
  leftButtonText: string
  rightButtonText: string
  onRightButtonClick?: () => void
  onLeftButtonClick?: () => void
  leadTime?: number
  isTradeDiscountAvailable?: boolean
}

const StyledPrice = styled(Text)`
  ${({ shouldDisplayTradePrice }) =>
    shouldDisplayTradePrice &&
    css`
      text-decoration: line-through;
    `}
`

export const CartModal: React.FC<React.PWC<CartModalProps>> = ({
  title,
  price,
  tradePrice,
  cartQuantity,
  optionValues,
  quantityUnit,
  cartTotalPrice,
  cartItemsCount,
  imageSrc,
  imageAlt,
  isOpen,
  onModalClose,
  leftButtonText,
  rightButtonText,
  onRightButtonClick,
  onLeftButtonClick,
  leadTime,
  isTradeDiscountAvailable,
  ...rest
}) => {
  const shouldDisplayTradePrice =
    isTradeDiscountAvailable && tradePrice.total > 0
  return (
    <StyledSlideOutModal
      {...rest}
      data-test-id={DATA_TEST_ID.CART_MODAL}
      ariaLabel={MODAL_TITLE}
      isOpen={isOpen}
      onModalClose={onModalClose}
      customTitle={() => (
        <Flex>
          <Icon.Checkmark color={COLOR_INTENT.CART_MODAL.TITLE} />
          <Box ml={SPACE.PX_10}>
            <H5 color={COLOR_INTENT.CART_MODAL.TITLE} mb={0}>
              {MODAL_TITLE}
            </H5>
          </Box>
        </Flex>
      )}
      customFooter={() => (
        <Flex
          justifyContent="space-between"
          flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
          p={{
            MOBILE: `${SPACE.PX_20} ${SPACE.PX_15}`,
            TABLET: `${SPACE.PX_20} ${SPACE.PX_30}`,
          }}
          mb={{ MOBILE: SPACE.PX_30, TABLET: 0 }}
        >
          <Flex
            flexDirection="column"
            mb={{ MOBILE: SPACE.PX_15, TABLET: 0 }}
            alignItems={{ MOBILE: 'center', TABLET: 'start' }}
          >
            <Text fontWeight={FONT_WEIGHT.SEMI_BOLD}>
              Cart subtotal (
              <span data-test-id={DATA_TEST_ID.CART_MODAL_ITEMS_COUNT}>
                {cartItemsCount}
              </span>
              {` ${cartItemsCount === 1 ? 'item' : 'items'}): `}
              <Text data-test-id={DATA_TEST_ID.CART_MODAL_ITEMS_SUBTOTAL}>
                {formatPrice({
                  price: cartTotalPrice?.total,
                  currency: cartTotalPrice?.currencyIsoCode,
                })}
              </Text>
            </Text>
            <Text fontSize={FONT_SIZE.PX_12}>
              Shipping options are calculated at checkout
            </Text>
          </Flex>
          <Flex justifyContent="center" alignItems="center">
            <Button
              data-test-id={DATA_TEST_ID.CART_MODAL_CART_REDIRECT_BUTTON}
              width="initial"
              mr={SPACE.PX_10}
              variant={BUTTON_VARIANT.OUTLINE}
              onClick={onLeftButtonClick}
            >
              {leftButtonText}
            </Button>
            <Button
              width="initial"
              iconPosition={BUTTON_ICON_POSITION.RIGHT}
              icon={Icon.ChevronRightSmall}
              onClick={onRightButtonClick}
            >
              {rightButtonText}
            </Button>
          </Flex>
        </Flex>
      )}
    >
      <Card
        hasBorders
        paddingSize={CARD_PADDING_SIZE.NONE}
        flexDirection="column"
      >
        <Flex>
          <ProductImage
            src={imageSrc}
            alt={imageAlt}
            width={rem(IMAGE_BASE_WIDTH.PX_80)}
          />
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems={{ MOBILE: 'inherit', TABLET: 'flex-start' }}
            p={{ MOBILE: SPACE.PX_15, TABLET: SPACE.PX_20 }}
          >
            <Flex flexDirection="column">
              <H6
                data-test-id={DATA_TEST_ID.CART_MODAL_PRODUCT_TITLE}
                mb={SPACE.PX_4}
                pr={SPACE.PX_15}
                fontsSize={FONT_SIZE.PX_14}
              >
                {title}
              </H6>
              {optionValues.map((option, idx) => (
                <ProductOptionRow
                  key={idx}
                  name={option.productOption.name}
                  value={option.value}
                />
              ))}
              {leadTime && (
                <ProductDescription>
                  {formatLeadTimeString(leadTime)}
                </ProductDescription>
              )}
              <Text>
                {`Qty ${formatQuantityWithUnit({
                  quantity: cartQuantity,
                  unit: quantityUnit,
                  isUnitInParentheses: true,
                })}`}
              </Text>
            </Flex>
            <Box>
              {shouldDisplayTradePrice && (
                <Text
                  fontSize={FONT_SIZE.PX_16}
                  color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                  as="p"
                >
                  {formatPrice({
                    price: tradePrice.total,
                    currency: tradePrice.currencyIsoCode,
                  })}
                </Text>
              )}
              <StyledPrice shouldDisplayTradePrice={shouldDisplayTradePrice}>
                {formatPrice({
                  price: price.total,
                  currency: price.currencyIsoCode,
                })}
              </StyledPrice>
            </Box>
          </Flex>
        </Flex>
      </Card>
    </StyledSlideOutModal>
  )
}
